import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
// Material UI & Lucide Icons (Replacing Ant Design icons)
// Keep MUI for gradual migration (to be removed later)
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Avatar,
  FormControlLabel,
  Checkbox,
  Fade,
} from '@mui/material';
import {
  Plus as AddIcon,
  CheckSquare as TaskIcon,
  Edit3 as EditIcon,
  Camera as CameraIcon,
  Crop as CropIcon,
  X as XIcon,
  Check as CheckIcon,
  AlertCircle,
  DollarSign,
} from 'lucide-react';

import SideMenu from './components/SideMenu/SideMenu';
import SideMenuMobile from './components/SideMenu/SideMenuMobile';
import Header from './components/Header/Header';
import AppNavbar from './components/Header/AppNavbar';
import MainGrid from './components/MainDashboard/MainGrid';
import ProjectsView from './components/Projects/ProjectsView';
import TimelineView from './components/Timeline/TimelineView';
import TeamView from './components/Team/TeamView';
import SettingsView from './components/Settings/SettingsView';
import HelpView from './components/Help/HelpView';
import { Project, Task, TeamMember, UserProfile, AppUser, SystemSettings as AppSettings } from './types';
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
// Simple Login Component
const SimpleLogin: React.FC<{ onLogin: (user: AppUser) => void; users: AppUser[] }> = ({ onLogin, users }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    
    try {
      // Find user with matching credentials
      const user = users.find(u => 
        u.email.toLowerCase() === email.toLowerCase() && 
        u.password === password &&
        u.isActive
      );
      
      if (user) {
        // Update last login time
        const updatedUser = { ...user, lastLogin: new Date().toISOString() };
        
        // Update users database with latest login time
        const updatedUsers = users.map(u => 
          u.id === user.id ? updatedUser : u
        );
        localStorage.setItem('app_users', JSON.stringify(updatedUsers));
        
        // Store authentication data
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));
        localStorage.setItem('authToken', `token_${user.id}_${Date.now()}`);
        
        // Simulate network delay
        setTimeout(() => {
          onLogin(updatedUser);
          setIsLoading(false);
        }, 800);
      } else {
        setLoginError('Invalid email or password. Please check your credentials.');
        setIsLoading(false);
      }
    } catch (error) {
      setLoginError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    setSignupSuccess(false);

    // Validation
    if (!signupName.trim()) {
      setSignupError('Name is required');
      return;
    }
    if (!signupEmail.trim()) {
      setSignupError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail)) {
      setSignupError('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    if (users.some(u => u.email.toLowerCase() === signupEmail.toLowerCase())) {
      setSignupError('This email address is already registered');
      return;
    }

    // Show success message
    setSignupSuccess(true);
    // Reset form after delay
    setTimeout(() => {
      setSignupName('');
      setSignupEmail('');
      setSignupSuccess(false);
      setSignupOpen(false);
    }, 2000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        position: { xs: 'relative', md: 'fixed' },
        top: 0,
        left: 0,
        margin: 0,
        overflow: { xs: 'hidden', md: 'auto' },
        background: { 
          xs: 'white',
          md: 'linear-gradient(135deg, rgba(25, 22, 78, 0.95) 0%, rgba(25, 22, 78, 0.85) 50%, rgba(25, 22, 78, 0.95) 100%), url("/Photos/buildings.webp")'
        },
        backgroundSize: { xs: 'cover', md: 'cover' },
        backgroundPosition: { xs: 'center', md: 'center center' },
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'scroll' },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: { xs: 'flex-start', md: 'center' },
        padding: { xs: 0, md: 2 },
      }}
    >
      {/* Mobile Header Cover Image */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          width: '100%',
          height: '35vh',
          background: 'linear-gradient(135deg, rgba(25, 22, 78, 0.85) 0%, rgba(25, 22, 78, 0.7) 99%), url("/Photos/buildings.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.2) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)',
          }
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            color: 'white',
            textAlign: 'center',
            px: 3,
            position: 'relative',
            zIndex: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}
        >
          Welcome to Investcorp
        </Typography>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            color: '#f59f1f',
            textAlign: 'center',
            px: 3,
            position: 'relative',
            zIndex: 1,
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            mt: 1
          }}
        >
          Project Management System
        </Typography>
      </Box>

      {/* Left Side - Welcome Text (Desktop Only) */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          color: 'white',
          pr: 4,
          maxWidth: '500px',
        }}
      >
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { md: '2.5rem', lg: '3rem' }, color: 'white' }}>
          Welcome to Investcorp
        </Typography>
        
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 300, mb: 3, color: '#f59f1f' }}>
          Project Management System
        </Typography>
        
        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, maxWidth: '400px', opacity: 0.9 }}>
          Streamline your construction and engineering projects with our comprehensive management platform. 
          Track progress, manage resources, and deliver excellence.
        </Typography>
      </Box>

      {/* Right Side - Login Form */}
      <Box sx={{ width: { xs: '100%', md: '400px' }, maxWidth: 400, px: { xs: 3, md: 0 }, pb: { xs: 3, md: 0 }, mt: { xs: -6, md: 0 } }}>
        <Card sx={{ 
          background: 'rgba(255, 255, 255, 1)',
          backdropFilter: 'blur(10px)',
          borderRadius: { xs: '24px 24px 0 0', md: 3 },
          boxShadow: { xs: '0 -4px 20px rgba(0, 0, 0, 0.1)', md: '0 20px 40px rgba(0, 0, 0, 0.3)' },
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <CardContent sx={{ p: { xs: 4, md: 4 } }}>
            <Box sx={{ mb: 4 }}>
              <Typography component="h1" variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#2c2c2c', fontSize: '1.5rem', mb: 3 }}>
                Sign in
              </Typography>
            </Box>

            <form onSubmit={handleLogin}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block', fontSize: '0.75rem' }}>
                  Email
                </Typography>
                <TextField
                  required
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      border: 'none',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        boxShadow: '0 0 0 2px #f59f1f',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '12px 14px',
                      fontSize: '0.95rem',
                    },
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block', fontSize: '0.75rem' }}>
                  Password
                </Typography>
                <TextField
                  required
                  fullWidth
                  name="password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#f8f9fa',
                      border: 'none',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#fff',
                        boxShadow: '0 0 0 2px #f59f1f',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                    '& .MuiInputBase-input': {
                      padding: '12px 14px',
                      fontSize: '0.95rem',
                    },
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={<Checkbox value="remember" sx={{ color: '#2c2c2c', '&.Mui-checked': { color: '#f59f1f' } }} />}
                  label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Remember Me</Typography>}
                />
                <Typography variant="body2" sx={{ color: '#f59f1f', fontSize: '0.85rem', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                  Forgot Password?
                </Typography>
              </Box>

              {loginError && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                  <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    ⚠️ {loginError}
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ 
                  mt: 0, 
                  mb: 3, 
                  bgcolor: '#2c2c2c',
                  borderRadius: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { 
                    bgcolor: '#0f0d3a',
                    boxShadow: 'none'
                  },
                  '&:disabled': { bgcolor: '#ccc' }
                }}
              >
                {isLoading ? 'Signing In...' : 'Login'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.85rem', mb: 1 }}>
                  Don't have an account? <Typography component="span" sx={{ color: '#f59f1f', cursor: 'pointer', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }} onClick={() => setSignupOpen(true)}>Sign up</Typography>
                </Typography>
              </Box>
            </form>

          </CardContent>
        </Card>
      </Box>

      {/* Sign Up Dialog */}
      <Dialog open={signupOpen} onClose={() => !signupSuccess && setSignupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#2c2c2c', color: 'white', fontWeight: 'bold' }}>
          Create New Account
        </DialogTitle>
        {/* Mobile Cover Image with Gradient */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            width: '100%',
            height: '180px',
            background: 'linear-gradient(135deg, rgba(25, 22, 78, 0.85) 0%, rgba(25, 22, 78, 0.7) 99%), url("/Photos/buildings.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        />
        <DialogContent sx={{ mt: 2 }}>
          {signupSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h6" sx={{ color: '#4caf50', mb: 1 }}>
                ✓ Account Creation Initiated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thank you for signing up! Please contact the administrator to activate your account.
              </Typography>
            </Box>
          ) : (
            <form onSubmit={handleSignup}>
              <TextField
                autoFocus
                margin="dense"
                label="Full Name"
                type="text"
                fullWidth
                variant="outlined"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
              <TextField
                margin="dense"
                label="Email Address"
                type="email"
                fullWidth
                variant="outlined"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
              {signupError && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                  <Typography variant="body2" color="error">
                    {signupError}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setSignupOpen(false)}
                  sx={{ color: '#666' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: 1,
                    bgcolor: '#2c2c2c',
                    '&:hover': { bgcolor: '#0f0d3a' }
                  }}
                >
                  Sign Up
                </Button>
              </Box>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Redundant interfaces removed and moved to src/types/index.ts

// 🔧 REAL ADMIN ACCOUNT SETUP - CUSTOMIZE THESE CREDENTIALS:
const defaultUsers: AppUser[] = [
  {
    id: 'admin-real-001',
    email: 'twizasimwanza@gmail.com', // ✅ Your real email
    password: '1simwanza', // ✅ Your password
    role: 'admin',
    isActive: true,
    createdAt: new Date().toISOString(),
    profile: {
      id: 1,
      firstName: 'Twiza', // ✅ Update with your name if desired
      lastName: 'Simwanza', // ✅ Update with your name if desired
      email: 'twizasimwanza@gmail.com', // ✅ Matches login email
      phone: '+260-XXX-XXXX', // 👈 CHANGE: Add your phone number
      role: 'System Administrator',
      department: 'IT Administration',
      // Avatar will be set when you upload a photo in Settings → User Profile
    }
  },
  {
    id: 'admin-investcorp-001',
    email: 'Investcorp@gmail.com',
    password: '123investcorp',
    role: 'admin',
    isActive: true,
    createdAt: new Date().toISOString(),
    profile: {
      id: 2,
      firstName: 'Investcorp',
      lastName: 'Admin',
      email: 'Investcorp@gmail.com',
      phone: '+260-XXX-XXXX',
      role: 'Administrator',
      department: 'Management',
    }
  }
  // 📝 NOTE: Additional users can be added through the Admin interface
  // Login with admin credentials → Settings → User Management → Add New User
];

// Main authenticated app component  
const AuthenticatedApp: React.FC<{ onLogout: () => void; currentUser: AppUser }> = ({ onLogout, currentUser }) => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [timelineView, setTimelineView] = useState<'month' | 'quarter' | 'year'>('quarter');
  const [projectFilter, setProjectFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [projectSort, setProjectSort] = useState<'name' | 'progress' | 'budget' | 'startDate'>('name');
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);
  const [teamMemberFilter, setTeamMemberFilter] = useState<'all' | 'available' | 'unavailable'>('all');
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile>(currentUser.profile);
  const [editedProfile, setEditedProfile] = useState<UserProfile>({ ...userProfile });
  const [imageCropOpen, setImageCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // Helper function to calculate progress based on completed tasks
  const calculateProgress = (tasks: Task[]) => {
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(task => task.completed).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // Helper function to determine project status based on tasks
  const getProjectStatus = (tasks: Task[], currentStatus: 'In Progress' | 'Active' | 'Completed' | 'On Hold'): 'In Progress' | 'Active' | 'Completed' | 'On Hold' => {
    if (tasks.length === 0) return currentStatus;
    const completedTasks = tasks.filter(task => task.completed).length;
    
    // If all tasks are completed, set status to Completed
    if (completedTasks === tasks.length && tasks.length > 0) {
      return 'Completed';
    }
    
    // If not all tasks are completed but status was previously Completed, change to Active
    if (completedTasks < tasks.length && currentStatus === 'Completed') {
      return 'Active';
    }
    
    // Otherwise, keep the current status (but not if it's manually set to "On Hold")
    return currentStatus;
  };

  const initialProjects = [
    {
      id: 1,
      name: 'Lusaka Industrial Complex Construction',
      description: 'Construction of a 50,000 sqm industrial complex with warehouses, office buildings, and manufacturing facilities in Lusaka East',
      progress: 0, // Will be calculated automatically
      status: 'Active' as const,
      budgetedAmount: 2500000,
      spentAmount: 1850000,
      currency: 'ZMW',
      startDate: '2024-03-15',
      // endDate will be set when all tasks are completed
      tasks: [
        { id: 1, title: 'Foundation and structural work', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 950000 },
        { id: 2, title: 'Electrical and plumbing systems', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 900000 },
        { id: 3, title: 'Interior finishing', completed: false, priority: 'High' as const, status: 'In Progress' as const },
        { id: 4, title: 'Exterior landscaping', completed: false, priority: 'Medium' as const, status: 'Not Started' as const },
        { id: 5, title: 'Final inspections', completed: false, priority: 'Medium' as const, status: 'Not Started' as const },
      ]
    },
    {
      id: 2,
      name: 'Kafue River Bridge Maintenance',
      description: 'Major structural maintenance and reinforcement of the Kafue River Bridge including deck resurfacing and cable inspection',
      progress: 0, // Will be calculated automatically
      status: 'Active' as const,
      budgetedAmount: 850000,
      spentAmount: 625000,
      currency: 'ZMW',
      startDate: '2024-06-20',
      // endDate will be set when all tasks are completed
      tasks: [
        { id: 6, title: 'Structural assessment', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 325000 },
        { id: 7, title: 'Traffic diversion setup', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 300000 },
        { id: 8, title: 'Deck surface preparation', completed: false, priority: 'High' as const, status: 'In Progress' as const },
        { id: 9, title: 'Cable tensioning', completed: false, priority: 'Medium' as const, status: 'Not Started' as const },
      ]
    },
    {
      id: 3,
      name: 'Copperbelt Mining Equipment Overhaul',
      description: 'Complete overhaul and modernization of heavy mining equipment fleet for Konkola Copper Mines including excavators, haul trucks, and processing machinery',
      progress: 0, // Will be calculated automatically
      status: 'Completed' as const,
      budgetedAmount: 1200000,
      spentAmount: 1198500,
      currency: 'ZMW',
      startDate: '2023-11-10',
      endDate: '2024-08-15',
      tasks: [
        { id: 10, title: 'Equipment inspection and assessment', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 350000 },
        { id: 11, title: 'Engine and hydraulic system overhaul', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 485000 },
        { id: 12, title: 'Electrical system upgrade', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 363500 },
        { id: 13, title: 'Performance testing', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 0 },
        { id: 14, title: 'Equipment delivery', completed: true, priority: 'Medium' as const, status: 'Completed' as const, actualCost: 0 },
      ]
    },
    {
      id: 4,
      name: 'Ndola Airport Runway Extension',
      description: 'Extension and upgrade of Ndola Airport runway from 2.4km to 3.2km including new lighting systems, drainage, and surface treatment',
      progress: 0, // Will be calculated automatically
      status: 'Active' as const,
      budgetedAmount: 4500000,
      spentAmount: 350000,
      currency: 'ZMW',
      startDate: '2024-09-01',
      // endDate will be set when all tasks are completed
      tasks: [
        { id: 15, title: 'Environmental impact assessment', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 175000 },
        { id: 16, title: 'Site preparation and clearing', completed: true, priority: 'High' as const, status: 'Completed' as const, actualCost: 175000 },
        { id: 17, title: 'Earthworks and grading', completed: false, priority: 'High' as const, status: 'In Progress' as const },
        { id: 18, title: 'Concrete pouring and finishing', completed: false, priority: 'High' as const, status: 'Not Started' as const },
        { id: 19, title: 'Lighting system installation', completed: false, priority: 'Medium' as const, status: 'Not Started' as const },
        { id: 20, title: 'Safety testing and certification', completed: false, priority: 'Medium' as const, status: 'Not Started' as const },
      ]
    }
  ];

  const [projects, setProjects] = useState<Project[]>(
    initialProjects.map(project => ({
      ...project,
      progress: calculateProgress(project.tasks), // Calculate progress based on tasks
      status: getProjectStatus(project.tasks, project.status) // Auto-determine status based on tasks
    }))
  );

  // Sample team data for Zambian construction professionals
  const initialTeamMembers: TeamMember[] = [
    {
      id: 1,
      firstName: 'Mwangi',
      lastName: 'Kambole',
      email: 'mwangi.kambole@investcorp.zm',
      phone: '+260 97 123 4567',
      role: 'Senior Site Engineer',
      department: 'Engineering',
      employeeId: 'INV-ENG-001',
      status: 'Available',
      skills: ['Structural Engineering', 'Concrete Work', 'Project Planning', 'CAD Design'],
      certifications: [
        { name: 'Professional Engineer License', expiryDate: '2026-03-15', status: 'Valid' },
        { name: 'Safety Officer Certification', expiryDate: '2025-12-01', status: 'Expiring Soon' }
      ],
      safetyRating: 5,
      experienceLevel: 'Senior',
      currentAssignments: [
        { projectId: 1, projectName: 'Lusaka Industrial Complex', role: 'Lead Engineer', workloadPercentage: 80 }
      ],
      performanceMetrics: {
        completionRate: 94,
        qualityScore: 92,
        safetyScore: 98,
        punctuality: 96
      },
      joinDate: '2020-01-15',
      lastTrainingDate: '2024-09-20',
      location: 'Lusaka'
    },
    {
      id: 2,
      firstName: 'Chanda',
      lastName: 'Mulenga',
      email: 'chanda.mulenga@investcorp.zm',
      phone: '+260 96 234 5678',
      role: 'Equipment Operator',
      department: 'Operations',
      employeeId: 'INV-OPS-002',
      status: 'Available',
      skills: ['Crane Operation', 'Excavator Operation', 'Heavy Machinery Maintenance'],
      certifications: [
        { name: 'Crane Operator License', expiryDate: '2025-08-30', status: 'Valid' },
        { name: 'Heavy Equipment Certificate', expiryDate: '2025-11-15', status: 'Expiring Soon' }
      ],
      safetyRating: 4,
      experienceLevel: 'Mid-Level',
      currentAssignments: [],
      performanceMetrics: {
        completionRate: 88,
        qualityScore: 85,
        safetyScore: 91,
        punctuality: 89
      },
      joinDate: '2021-06-10',
      lastTrainingDate: '2024-08-15',
      location: 'Lusaka'
    },
    {
      id: 3,
      firstName: 'Beatrice',
      lastName: 'Phiri',
      email: 'beatrice.phiri@investcorp.zm',
      phone: '+260 95 345 6789',
      role: 'Project Manager',
      department: 'Management',
      employeeId: 'INV-MGT-003',
      status: 'Available',
      skills: ['Project Management', 'Budget Planning', 'Risk Assessment', 'Team Leadership'],
      certifications: [
        { name: 'PMP Certification', expiryDate: '2026-12-31', status: 'Valid' },
        { name: 'Construction Management Certificate', expiryDate: '2025-10-20', status: 'Valid' }
      ],
      safetyRating: 5,
      experienceLevel: 'Lead',
      currentAssignments: [
        { projectId: 2, projectName: 'Kafue River Bridge Maintenance', role: 'Project Manager', workloadPercentage: 90 }
      ],
      performanceMetrics: {
        completionRate: 96,
        qualityScore: 94,
        safetyScore: 99,
        punctuality: 98
      },
      joinDate: '2018-03-01',
      lastTrainingDate: '2024-10-01',
      location: 'Kafue'
    },
    {
      id: 4,
      firstName: 'Joseph',
      lastName: 'Banda',
      email: 'joseph.banda@investcorp.zm',
      phone: '+260 97 456 7890',
      role: 'Safety Officer',
      department: 'Safety & Compliance',
      employeeId: 'INV-SAF-004',
      status: 'Available',
      skills: ['Safety Inspection', 'Compliance Auditing', 'Risk Management', 'Emergency Response'],
      certifications: [
        { name: 'Safety Management Certificate', expiryDate: '2026-05-15', status: 'Valid' },
        { name: 'First Aid Certification', expiryDate: '2025-02-28', status: 'Expiring Soon' }
      ],
      safetyRating: 5,
      experienceLevel: 'Senior',
      currentAssignments: [
        { projectId: 1, projectName: 'Lusaka Industrial Complex', role: 'Safety Officer', workloadPercentage: 60 },
        { projectId: 4, projectName: 'Ndola Airport Runway Extension', role: 'Safety Consultant', workloadPercentage: 30 }
      ],
      performanceMetrics: {
        completionRate: 99,
        qualityScore: 97,
        safetyScore: 100,
        punctuality: 97
      },
      joinDate: '2019-07-15',
      lastTrainingDate: '2024-09-10',
      location: 'Multi-Site'
    },
    {
      id: 5,
      firstName: 'Grace',
      lastName: 'Zulu',
      email: 'grace.zulu@investcorp.zm',
      phone: '+260 96 567 8901',
      role: 'Electrical Engineer',
      department: 'Engineering',
      employeeId: 'INV-ENG-005',
      status: 'Unavailable',
      skills: ['Electrical Systems', 'Power Distribution', 'Control Systems', 'Troubleshooting'],
      certifications: [
        { name: 'Electrical Engineering License', expiryDate: '2026-09-30', status: 'Valid' },
        { name: 'High Voltage Certification', expiryDate: '2024-12-15', status: 'Expired' }
      ],
      safetyRating: 4,
      experienceLevel: 'Mid-Level',
      currentAssignments: [],
      performanceMetrics: {
        completionRate: 87,
        qualityScore: 90,
        safetyScore: 88,
        punctuality: 92
      },
      joinDate: '2022-01-20',
      lastTrainingDate: '2024-10-15',
      location: 'Lusaka'
    },
    {
      id: 6,
      firstName: 'Patrick',
      lastName: 'Mwamba',
      email: 'patrick.mwamba@investcorp.zm',
      phone: '+260 95 678 9012',
      role: 'Site Supervisor',
      department: 'Operations',
      employeeId: 'INV-OPS-006',
      status: 'Unavailable',
      skills: ['Team Supervision', 'Quality Control', 'Site Management', 'Resource Coordination'],
      certifications: [
        { name: 'Supervisory Management Certificate', expiryDate: '2025-07-20', status: 'Valid' },
        { name: 'Quality Control Certification', expiryDate: '2025-04-10', status: 'Valid' }
      ],
      safetyRating: 4,
      experienceLevel: 'Senior',
      currentAssignments: [],
      performanceMetrics: {
        completionRate: 91,
        qualityScore: 89,
        safetyScore: 93,
        punctuality: 88
      },
      joinDate: '2020-08-05',
      lastTrainingDate: '2024-07-30',
      location: 'Ndola'
    }
  ];

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(initialTeamMembers);

  // Settings state management
  const [settings, setSettings] = useState<AppSettings>({
    userProfile: userProfile,
    notifications: {
      projectUpdates: true,
      taskAssignments: true,
      milestoneAlerts: true,
      budgetAlerts: true,
      teamUpdates: true,
      safetyAlerts: true,
      emailEnabled: true,
      smsEnabled: false,
      inAppEnabled: true,
      frequency: 'Immediate'
    },
    company: {
      name: 'Investcorp Construction Ltd.',
      address: 'Plot 1234, Great East Road, Lusaka, Zambia',
      phone: '+260 211 123456',
      email: 'info@investcorp.zm',
      website: 'www.investcorp.zm',
      registrationNumber: 'RC-12345-ZM',
      taxNumber: 'TIN-987654321'
    },
    appearance: {
      theme: 'Light',
      primaryColor: '#f59f1f',
      language: 'English',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '12h',
      currency: 'ZMW',
      measurementUnit: 'Metric'
    },
    system: {
      autoBackup: true,
      backupFrequency: 'Daily',
      sessionTimeout: 30,
      maxFileSize: 10,
      allowedFileTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.png'],
      twoFactorAuth: false
    }
  });
  const [settingsActiveTab, setSettingsActiveTab] = useState('profile');
  const [appUsers, setAppUsers] = useState<AppUser[]>([]);

  // New User Management State
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee' as 'admin' | 'manager' | 'employee',
    department: '',
    phone: ''
  });

  // Edit User State
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [editUserData, setEditUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'employee' as 'admin' | 'manager' | 'employee',
    department: '',
    phone: '',
    isActive: true
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ 
    name: '', 
    description: '',
    budgetedAmount: 0,
    currency: 'ZMW',
    startDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days from today
    tasks: [] as { title: string; priority: 'High' | 'Medium' | 'Low' }[]
  });
  const [newTask, setNewTask] = useState({ title: '', priority: 'Medium' as 'High' | 'Medium' | 'Low' });
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingTaskData, setEditingTaskData] = useState({ title: '', priority: 'Medium' as 'High' | 'Medium' | 'Low' });
  const [projectDetailsOpen, setProjectDetailsOpen] = useState(false);
  const [projectEditOpen, setProjectEditOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [taskCostDialogOpen, setTaskCostDialogOpen] = useState(false);
  const [taskBeingCompleted, setTaskBeingCompleted] = useState<{ projectId: number; taskId: number } | null>(null);
  const [taskCost, setTaskCost] = useState<number>(0);
  const [endDateDialogOpen, setEndDateDialogOpen] = useState(false);
  const [projectNeedingEndDate, setProjectNeedingEndDate] = useState<number | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Update userProfile when currentUser changes (for switching between users)
  useEffect(() => {
    setUserProfile(currentUser.profile);
    setEditedProfile({ ...currentUser.profile });
  }, [currentUser]);

  // Sync settings userProfile with main userProfile whenever userProfile changes
  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      userProfile: userProfile
    }));
  }, [userProfile]);

  // Load data from localStorage on component mount
  useEffect(() => {
    loadAppDataFromLocalStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load users data on component mount
  useEffect(() => {
    const savedUsers = localStorage.getItem('app_users');
    if (savedUsers) {
      try {
        setAppUsers(JSON.parse(savedUsers));
      } catch (error) {
        console.error('Error loading users:', error);
        setAppUsers(defaultUsers);
      }
    } else {
      setAppUsers(defaultUsers);
    }
  }, []);

  // Save data to localStorage whenever key data changes
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      saveAppDataToLocalStorage();
    }, 1000); // Debounce saves by 1 second

    return () => clearTimeout(saveTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, projects, settings, teamMembers]);

  // Save data before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveAppDataToLocalStorage();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveAppDataToLocalStorage();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddTask = () => {
    if (newTask.title.trim()) {
      const task = {
        title: newTask.title.trim(),
        priority: newTask.priority
      };
      setNewProject({
        ...newProject,
        tasks: [...newProject.tasks, task]
      });
      setNewTask({ title: '', priority: 'Medium' });
    }
  };

  const handleRemoveTask = (index: number) => {
    setNewProject({
      ...newProject,
      tasks: newProject.tasks.filter((_, i) => i !== index)
    });
  };

  const handleCreateProject = () => {
    if (newProject.name && newProject.description) {
      const project: Project = {
        id: Date.now(),
        name: newProject.name,
        description: newProject.description,
        progress: 0, // Will be 0 since tasks start as incomplete
        status: 'Active',
        budgetedAmount: newProject.budgetedAmount,
        spentAmount: 0,
        currency: newProject.currency,
        startDate: newProject.startDate,
        // endDate is only set when all tasks are completed
        tasks: newProject.tasks.map((task, index) => ({
          id: Date.now() + index, // Generate unique IDs for tasks
          title: task.title,
          completed: false,
          priority: task.priority,
          status: 'Not Started' as const
        }))
      };
      setProjects([...projects, project]);
      setNewProject({ name: '', description: '', budgetedAmount: 0, currency: 'ZMW', startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], tasks: [] });
      setNewTask({ title: '', priority: 'Medium' });
      setDialogOpen(false);
    }
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleSaveProfile = () => {
    setUserProfile({ ...editedProfile });
    setProfileDialogOpen(false);
  };

  const handleCancelEdit = () => {
    setEditedProfile({ ...userProfile });
    setProfileDialogOpen(false);
  };

  const getUserInitials = () => {
    if (!userProfile.firstName || !userProfile.lastName) return 'User';
    return `${userProfile.firstName[0]}${userProfile.lastName[0]}`.toUpperCase();
  };

  const getUserFullName = () => {
    return `${userProfile.firstName} ${userProfile.lastName}`;
  };

  const handleDeleteTeamMember = (memberId: number) => {
    const member = teamMembers.find(m => m.id === memberId);
    if (member && window.confirm(`Are you sure you want to delete ${member.firstName} ${member.lastName}?`)) {
      setTeamMembers(teamMembers.filter(m => m.id !== memberId));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result?.toString() || '');
        setImageCropOpen(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleCropComplete = (crop: PixelCrop) => {
    setCompletedCrop(crop);
  };

  const generateCroppedImage = async () => {
    if (completedCrop && imgRef.current && previewCanvasRef.current) {
      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = completedCrop.width;
      canvas.height = completedCrop.height;

      ctx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );

      return new Promise<string>((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) return;
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      });
    }
  };

  const handleCropSave = async () => {
    const croppedImageUrl = await generateCroppedImage();
    if (croppedImageUrl) {
      setEditedProfile({ ...editedProfile, avatar: croppedImageUrl });
      setImageCropOpen(false);
      setImageSrc('');
    }
  };

  const handleViewProjectDetails = (project: Project) => {
    setSelectedProject(project);
    setProjectDetailsOpen(true);
  };

  const handleEditProject = (project: Project) => {
    setEditingProject({ ...project });
    setNewTask({ title: '', priority: 'Medium' }); // Reset new task form
    setProjectEditOpen(true);
  };

  const handleSaveProject = () => {
    if (editingProject) {
      // Recalculate progress and auto-update status based on tasks when saving
      const updatedProject = {
        ...editingProject,
        progress: calculateProgress(editingProject.tasks),
        status: getProjectStatus(editingProject.tasks, editingProject.status)
      };
      setProjects(projects.map(p => 
        p.id === editingProject.id ? updatedProject : p
      ));
      setProjectEditOpen(false);
      setEditingProject(null);
    }
  };

  // Function to toggle task completion
  const handleToggleTaskCompletion = (projectId: number, taskId: number) => {
    const project = projects.find(p => p.id === projectId);
    const task = project?.tasks.find(t => t.id === taskId);
    
    // If marking as complete and task doesn't have a cost yet, open cost dialog
    if (task && !task.completed) {
      setTaskBeingCompleted({ projectId, taskId });
      setTaskCost(0);
      setTaskCostDialogOpen(true);
      return;
    }
    
    // If unmarking as complete, remove the cost and update budget
    const updatedProjects = projects.map(project => {
      if (project.id === projectId) {
        const updatedTasks = project.tasks.map(task => {
          if (task.id === taskId) {
            const updatedTask = { ...task, completed: !task.completed };
            return updatedTask;
          }
          return task;
        });
        
        // Recalculate spentAmount
        const newSpentAmount = updatedTasks
          .filter(t => t.completed && t.actualCost)
          .reduce((sum, t) => sum + (t.actualCost || 0), 0);
        
        // Check if all tasks are completed
        const allTasksCompleted = updatedTasks.every(t => t.completed);
        
        const updatedProject = {
          ...project,
          tasks: updatedTasks,
          spentAmount: newSpentAmount,
          progress: calculateProgress(updatedTasks),
          status: getProjectStatus(updatedTasks, project.status),
          // If not all tasks are completed, clear the endDate to allow user to re-enter it
          ...(allTasksCompleted ? {} : { endDate: undefined })
        };
        
        if (selectedProject && selectedProject.id === projectId) {
          setSelectedProject(updatedProject);
        }
        
        return updatedProject;
      }
      return project;
    });
    
    setProjects(updatedProjects);
  };

  const handleSaveTaskCost = () => {
    if (!taskBeingCompleted) return;
    
    const { projectId, taskId } = taskBeingCompleted;
    
    const updatedProjects = projects.map(project => {
      if (project.id === projectId) {
        const updatedTasks = project.tasks.map(task => 
          task.id === taskId 
            ? { ...task, completed: true, actualCost: taskCost, status: 'Completed' as const } 
            : task
        );
        
        // Calculate total spent amount from all completed tasks
        const newSpentAmount = updatedTasks
          .filter(t => t.completed && t.actualCost !== undefined)
          .reduce((sum, t) => sum + (t.actualCost || 0), 0);
        
        // Check if all tasks are completed
        const allTasksCompleted = updatedTasks.every(t => t.completed);
        
        const updatedProject = {
          ...project,
          tasks: updatedTasks,
          spentAmount: newSpentAmount,
          progress: calculateProgress(updatedTasks),
          status: getProjectStatus(updatedTasks, project.status)
          // Don't set endDate here - let user choose it
        };
        
        if (selectedProject && selectedProject.id === projectId) {
          setSelectedProject(updatedProject);
        }
        
        // If all tasks are completed and no endDate set, open dialog for user to choose
        if (allTasksCompleted && !project.endDate) {
          setProjectNeedingEndDate(projectId);
          setSelectedEndDate(new Date().toISOString().split('T')[0]);
          setEndDateDialogOpen(true);
        }
        
        return updatedProject;
      }
      return project;
    });
    
    setProjects(updatedProjects);
    setTaskCostDialogOpen(false);
    setTaskBeingCompleted(null);
    setTaskCost(0);
  };

  const handleSaveEndDate = () => {
    if (!projectNeedingEndDate) return;

    const updatedProjects = projects.map(project => {
      if (project.id === projectNeedingEndDate) {
        return {
          ...project,
          endDate: selectedEndDate
        };
      }
      return project;
    });

    setProjects(updatedProjects);
    
    // Update selected project if it's the one being edited
    if (selectedProject && selectedProject.id === projectNeedingEndDate) {
      setSelectedProject({
        ...selectedProject,
        endDate: selectedEndDate
      });
    }

    setEndDateDialogOpen(false);
    setProjectNeedingEndDate(null);
    setSelectedEndDate(new Date().toISOString().split('T')[0]);
  };

  const handleAddTaskToEditingProject = () => {
    if (newTask.title.trim() && editingProject) {
      const task: Task = {
        id: Date.now() + Math.random(), // Generate unique ID
        title: newTask.title.trim(),
        completed: false,
        priority: newTask.priority,
        status: 'Not Started' as const
      };
      const updatedTasks = [...editingProject.tasks, task];
      setEditingProject({
        ...editingProject,
        tasks: updatedTasks,
        status: getProjectStatus(updatedTasks, editingProject.status) // Auto-update status
      });
      setNewTask({ title: '', priority: 'Medium' });
    }
  };

  const handleRemoveTaskFromEditingProject = (taskId: number) => {
    if (editingProject) {
      const updatedTasks = editingProject.tasks.filter(task => task.id !== taskId);
      setEditingProject({
        ...editingProject,
        tasks: updatedTasks,
        status: getProjectStatus(updatedTasks, editingProject.status) // Auto-update status
      });
    }
  };

  const handleUpdateTaskInEditingProject = (taskId: number, updatedTask: Partial<Task>) => {
    if (editingProject) {
      const updatedTasks = editingProject.tasks.map(task =>
        task.id === taskId ? { ...task, ...updatedTask } : task
      );
      setEditingProject({
        ...editingProject,
        tasks: updatedTasks,
        status: getProjectStatus(updatedTasks, editingProject.status) // Auto-update status
      });
    }
  };

  const handleStartEditingTask = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTaskData({ title: task.title, priority: task.priority });
  };

  const handleSaveEditingTask = () => {
    if (editingTaskId && editingTaskData.title.trim()) {
      handleUpdateTaskInEditingProject(editingTaskId, {
        title: editingTaskData.title.trim(),
        priority: editingTaskData.priority
      });
      setEditingTaskId(null);
      setEditingTaskData({ title: '', priority: 'Medium' });
    }
  };

  const handleCancelEditingTask = () => {
    setEditingTaskId(null);
    setEditingTaskData({ title: '', priority: 'Medium' });
  };

  const handleCancelProjectEdit = () => {
    setEditingProject(null);
    setNewTask({ title: '', priority: 'Medium' });
    setEditingTaskId(null);
    setEditingTaskData({ title: '', priority: 'Medium' });
    setProjectEditOpen(false);
  };

  // Save settings function
  const handleSaveSettings = () => {
    // Update the main userProfile with changes from settings
    setUserProfile(settings.userProfile);
    setEditedProfile(settings.userProfile);
    
    // Update settings to keep userProfile in sync
    setSettings(prev => ({
      ...prev,
      userProfile: settings.userProfile
    }));
    
    // Update the user in the users database
    const updatedUsers = appUsers.map(user => {
      if (user.id === currentUser.id) {
        return {
          ...user,
          profile: settings.userProfile
        };
      }
      return user;
    });
    
    // Save updated users to localStorage
    setAppUsers(updatedUsers);
    localStorage.setItem('app_users', JSON.stringify(updatedUsers));
    
    // Update current user in localStorage
    const updatedCurrentUser = {
      ...currentUser,
      profile: settings.userProfile
    };
    localStorage.setItem('currentUser', JSON.stringify(updatedCurrentUser));
    
    console.log('Settings saved successfully:', settings);
    alert('Profile settings saved successfully!');
  };

  // New User Management Functions
  const handleAddNewUser = () => {
    setNewUserDialogOpen(true);
  };

  const handleNewUserSave = async () => {
    // Validation
    if (!newUserData.firstName || !newUserData.lastName || !newUserData.email || !newUserData.password) {
      alert('Please fill in all required fields');
      return;
    }

    // Check if email already exists
    if (appUsers.some(user => user.email.toLowerCase() === newUserData.email.toLowerCase())) {
      alert('Email already exists. Please use a different email.');
      return;
    }

    // Create new user
    const newUser: AppUser = {
      id: `user-${Date.now()}`,
      email: newUserData.email,
      password: newUserData.password,
      role: newUserData.role,
      isActive: true,
      createdAt: new Date().toISOString(),
      profile: {
        id: Date.now(),
        firstName: newUserData.firstName,
        lastName: newUserData.lastName,
        email: newUserData.email,
        phone: newUserData.phone || '+260-XXX-XXXX',
        role: newUserData.role === 'admin' ? 'System Administrator' : 
              newUserData.role === 'manager' ? 'Project Manager' : 'Team Member',
        department: newUserData.department || 'General',
      }
    };

    // Add to users array
    const updatedUsers = [...appUsers, newUser];
    setAppUsers(updatedUsers);
    
    // Save to localStorage
    localStorage.setItem('app_users', JSON.stringify(updatedUsers));

    // Send welcome email with login credentials
    const emailSent = await sendWelcomeEmail(newUser, newUserData.password);

    // Reset form and close dialog
    setNewUserData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'employee',
      department: '',
      phone: ''
    });
    setNewUserDialogOpen(false);
    
    // Show success message
    let message = `✅ User ${newUserData.firstName} ${newUserData.lastName} has been created successfully!`;
    
    if (!settings.notifications.emailEnabled) {
      message += `\n⚠️  Email notifications are disabled in settings. User not notified.`;
    } else if (emailSent) {
      message += `\n📧 Welcome email sent to ${newUserData.email}`;
    } else {
      message += `\n❌ Failed to send welcome email. Please notify user manually.`;
    }
    
    alert(message);
  };

  const handleNewUserCancel = () => {
    // Reset form and close dialog
    setNewUserData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'employee',
      department: '',
      phone: ''
    });
    setNewUserDialogOpen(false);
  };

  // Edit User Functions
  const handleEditUser = (user: AppUser) => {
    setEditingUser(user);
    setEditUserData({
      firstName: user.profile.firstName,
      lastName: user.profile.lastName,
      email: user.profile.email,
      role: user.role,
      department: user.profile.department,
      phone: user.profile.phone,
      isActive: user.isActive
    });
    setEditUserDialogOpen(true);
  };

  const handleEditUserSave = () => {
    if (!editingUser) return;

    // Validation
    if (!editUserData.firstName || !editUserData.lastName || !editUserData.email) {
      alert('Please fill in all required fields');
      return;
    }

    // Check if email already exists for other users
    if (appUsers.some(user => user.id !== editingUser.id && user.email.toLowerCase() === editUserData.email.toLowerCase())) {
      alert('Email already exists. Please use a different email.');
      return;
    }

    // Update user
    const updatedUser: AppUser = {
      ...editingUser,
      email: editUserData.email,
      role: editUserData.role,
      isActive: editUserData.isActive,
      profile: {
        ...editingUser.profile,
        firstName: editUserData.firstName,
        lastName: editUserData.lastName,
        email: editUserData.email,
        phone: editUserData.phone,
        role: editUserData.role === 'admin' ? 'System Administrator' : 
              editUserData.role === 'manager' ? 'Project Manager' : 'Team Member',
        department: editUserData.department,
      }
    };

    // Update users array
    const updatedUsers = appUsers.map(user => 
      user.id === editingUser.id ? updatedUser : user
    );
    setAppUsers(updatedUsers);
    
    // Save to localStorage
    localStorage.setItem('app_users', JSON.stringify(updatedUsers));

    // If editing current user, update current user in localStorage
    if (editingUser.id === currentUser.id) {
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
    }

    // Reset and close
    setEditingUser(null);
    setEditUserDialogOpen(false);
    
    alert(`User ${editUserData.firstName} ${editUserData.lastName} has been updated successfully!`);
  };

  const handleEditUserCancel = () => {
    setEditingUser(null);
    setEditUserDialogOpen(false);
  };

  const handleToggleUserStatus = (user: AppUser) => {
    // Prevent deactivating yourself
    if (user.id === currentUser.id) {
      alert('You cannot deactivate your own account.');
      return;
    }

    const action = user.isActive ? 'deactivate' : 'activate';
    const confirmMessage = `Are you sure you want to ${action} ${user.profile.firstName} ${user.profile.lastName}?`;
    
    if (window.confirm(confirmMessage)) {
      const updatedUser = { ...user, isActive: !user.isActive };
      const updatedUsers = appUsers.map(u => 
        u.id === user.id ? updatedUser : u
      );
      
      setAppUsers(updatedUsers);
      localStorage.setItem('app_users', JSON.stringify(updatedUsers));
      
      alert(`User ${user.profile.firstName} ${user.profile.lastName} has been ${user.isActive ? 'deactivated' : 'activated'}.`);
    }
  };

  const handleDeleteUser = (user: AppUser) => {
    // Prevent deleting yourself
    if (user.id === currentUser.id) {
      alert('You cannot delete your own account.');
      return;
    }

    // Prevent deleting the last admin
    const adminUsers = appUsers.filter(u => u.role === 'admin' && u.id !== user.id);
    if (user.role === 'admin' && adminUsers.length === 0) {
      alert('Cannot delete the last admin user. Please create another admin first.');
      return;
    }

    const confirmMessage = `Are you sure you want to permanently delete ${user.profile.firstName} ${user.profile.lastName}? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      const updatedUsers = appUsers.filter(u => u.id !== user.id);
      
      setAppUsers(updatedUsers);
      localStorage.setItem('app_users', JSON.stringify(updatedUsers));
      
      alert(`User ${user.profile.firstName} ${user.profile.lastName} has been deleted successfully.`);
    }
  };

  // Email notification function for new users
  const sendWelcomeEmail = async (user: AppUser, password: string) => {
    // Check if email notifications are enabled
    if (!settings.notifications.emailEnabled) {
      console.log('📧 Email notifications are disabled in settings');
      return false;
    }
    
    try {
      // Get auth token for API call
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.error('No authentication token found');
        return false;
      }

      // Call backend email service
      const response = await fetch('http://localhost:8000/email/send-welcome', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          to_email: user.email,
          user_name: `${user.profile.firstName} ${user.profile.lastName}`,
          user_password: password,
          user_role: user.role,
          department: user.profile.department
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('📧 Email sent successfully via backend service');
        return true;
      } else {
        console.error('Backend email service failed:', result.message);
        return false;
      }
      
    } catch (error) {
      console.error('Failed to send welcome email:', error);
      
      // Fallback: Show user the credentials directly if email fails
      const fallbackMessage = `
❌ Email delivery failed, but account was created successfully!

Please provide these credentials to ${user.profile.firstName} ${user.profile.lastName} manually:

📧 Email: ${user.email}
🔐 Password: ${password}
👤 Role: ${user.role}
🏢 Department: ${user.profile.department}
🔗 Login URL: ${window.location.origin}
      `;
      
      alert(fallbackMessage);
      return false;
    }
  };

  // Function to resend welcome email to existing users
  const handleResendWelcomeEmail = async (user: AppUser) => {
    if (!settings.notifications.emailEnabled) {
      alert('⚠️ Email notifications are disabled in settings. Please enable them first.');
      return;
    }

    const confirmMessage = `Send account information email to ${user.profile.firstName} ${user.profile.lastName} (${user.email})?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        // Get auth token for API call
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          alert('Authentication required. Please log in again.');
          return;
        }

        // Call backend email service
        const response = await fetch('http://localhost:8000/email/send-account-info', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            to_email: user.email,
            user_name: `${user.profile.firstName} ${user.profile.lastName}`,
            user_role: user.role,
            department: user.profile.department,
            is_active: user.isActive
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success) {
          alert(`📧 Account information email sent successfully to ${user.profile.firstName} ${user.profile.lastName} at ${user.email}`);
        } else {
          alert(`❌ Failed to send email: ${result.message}`);
        }
        
      } catch (error) {
        console.error('Failed to resend email:', error);
        alert(`❌ Failed to send email to ${user.email}. Please try again or check your connection.`);
      }
    }
  };

  const handleSettingsPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const newAvatarUrl = reader.result?.toString() || '';
        // Update the settings userProfile with the new avatar
        setSettings(prev => ({
          ...prev,
          userProfile: {
            ...prev.userProfile,
            avatar: newAvatarUrl
          }
        }));
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  // Data persistence functions
  const saveAppDataToLocalStorage = () => {
    // Create a copy of settings without userProfile (since it's user-specific)
    const { userProfile, ...settingsWithoutProfile } = settings;
    
    const appData = {
      // Don't save userProfile - it's managed per user in app_users localStorage
      projects,
      settings: settingsWithoutProfile,
      employees: teamMembers,
      timestamp: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('investcorp_app_data', JSON.stringify(appData));
      console.log('App data saved to localStorage');
    } catch (error) {
      console.error('Error saving app data:', error);
    }
  };

  const loadAppDataFromLocalStorage = () => {
    try {
      const savedData = localStorage.getItem('investcorp_app_data');
      if (savedData) {
        const appData = JSON.parse(savedData);
        
        // Only restore data if it's recent (within last 30 days)
        const savedTime = new Date(appData.timestamp);
        const now = new Date();
        const daysDiff = (now.getTime() - savedTime.getTime()) / (1000 * 60 * 60 * 24);
        
        if (daysDiff <= 30) {
          // Migrate old project data to include actualCost, startDate, and endDate
          if (appData.projects) {
            const migratedProjects = appData.projects.map((project: any) => {
              // Check if this is old data without actualCost, startDate, or endDate
              const needsMigration = project.tasks.some((t: any) => 
                t.completed && t.actualCost === undefined
              ) || !project.startDate || !project.endDate;
              
              if (needsMigration) {
                // Use fresh initialProjects data instead
                console.log('Migrating project data to include actualCost, startDate, and endDate fields');
                return null; // Will be filtered out
              }
              return project;
            }).filter(Boolean);
            
            // If any projects were migrated, use fresh initial data
            if (migratedProjects.length !== appData.projects.length) {
              setProjects(initialProjects.map(project => ({
                ...project,
                progress: calculateProgress(project.tasks),
                status: getProjectStatus(project.tasks, project.status)
              })));
              console.log('Using fresh initial data with actualCost, startDate, and endDate fields');
            } else {
              setProjects(appData.projects);
            }
          }
          
          if (appData.settings) {
            setSettings(prev => ({
              ...appData.settings,
              userProfile: prev.userProfile // Always preserve current user's profile
            }));
          }
          if (appData.employees) setTeamMembers(appData.employees);
          console.log('App data loaded from localStorage (user profile preserved)');
        } else {
          console.log('Saved data too old, using defaults');
        }
      }
    } catch (error) {
      console.error('Error loading app data:', error);
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return <MainGrid projects={projects} teamMembers={teamMembers} calculateProgress={calculateProgress} />;
      case 'projects':
        return (
          <ProjectsView
            projects={projects}
            projectFilter={projectFilter}
            setProjectFilter={setProjectFilter}
            projectSort={projectSort}
            setProjectSort={setProjectSort}
            calculateProgress={calculateProgress}
            setDialogOpen={setDialogOpen}
            expandedProjectId={expandedProjectId}
            setExpandedProjectId={setExpandedProjectId}
            handleViewProjectDetails={handleViewProjectDetails}
            handleEditProject={handleEditProject}
          />
        );
      case 'timeline':
        return (
          <TimelineView
            projects={projects}
            timelineView={timelineView}
            setTimelineView={setTimelineView}
          />
        );
      case 'team':
        return (
          <TeamView
            teamMembers={teamMembers}
            teamMemberFilter={teamMemberFilter}
            setTeamMemberFilter={setTeamMemberFilter}
            handleDeleteTeamMember={handleDeleteTeamMember}
          />
        );
      case 'settings':
        return (
          <SettingsView
            settings={settings}
            setSettings={setSettings}
            settingsActiveTab={settingsActiveTab}
            setSettingsActiveTab={setSettingsActiveTab}
            handleSaveSettings={handleSaveSettings}
            handleSettingsPhotoUpload={handleSettingsPhotoUpload}
            appUsers={appUsers}
            currentUser={currentUser}
            handleAddNewUser={handleAddNewUser}
            handleEditUser={handleEditUser}
            handleToggleUserStatus={handleToggleUserStatus}
            handleDeleteUser={handleDeleteUser}
            handleResendWelcomeEmail={handleResendWelcomeEmail}
            setAppUsers={setAppUsers}
            defaultUsers={defaultUsers}
          />
        );
      case 'help':
        return <HelpView />;
      default:
        return <MainGrid projects={projects} teamMembers={teamMembers} calculateProgress={calculateProgress} />;
    }
  };


  return (
    <>
      <Box sx={{ display: 'flex' }}>
      <SideMenu 
        currentView={currentView} 
        onViewChange={setCurrentView} 
        user={{
          name: getUserFullName(),
          email: userProfile.email,
          role: userProfile.role,
          avatar: userProfile.avatar
        }}
        getUserInitials={getUserInitials}
        onLogout={onLogout}
      />
      <SideMenuMobile 
        open={mobileDrawerOpen} 
        toggleDrawer={(newOpen: boolean) => () => setMobileDrawerOpen(newOpen)} 
        user={{
          name: getUserFullName(),
          email: userProfile.email,
          avatar: userProfile.avatar
        }}
        currentView={currentView}
        onViewChange={setCurrentView}
        onLogout={onLogout}
      />
      
      <Box
        component="main"
        sx={(theme) => ({
          flexGrow: 1,
          backgroundColor: alpha(theme.palette.background.default, 1),
          overflow: 'auto',
          minHeight: '100vh',
        })}
      >
        <AppNavbar onToggleDrawer={() => setMobileDrawerOpen(!mobileDrawerOpen)} />
        <Stack
          spacing={2}
          sx={{
            alignItems: 'center',
            mx: 3,
            pb: 5,
            mt: { xs: 8, md: 0 },
          }}
        >
          <Header currentView={currentView} />
          <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
            <Fade in={true} timeout={400} key={currentView}>
              <Box sx={{ p: 0 }}>
                {renderCurrentView()}
              </Box>
            </Fade>
          </Box>
        </Stack>
      </Box>
 
      


      {/* Profile Dropdown Menu */}
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            minWidth: '220px',
          }
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ px: 2, py: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          {userProfile.avatar ? (
            <Avatar src={userProfile.avatar} sx={{ width: 56, height: 56, border: '2px solid #f59f1f' }} />
          ) : (
            <Avatar sx={{ width: 56, height: 56, backgroundColor: '#f59f1f', color: '#2c2c2c', fontWeight: 'bold' }}>
              {getUserInitials()}
            </Avatar>
          )}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#2c2c2c' }}>
              {getUserFullName()}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666', display: 'block' }}>
              {userProfile.email}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666', display: 'block', mt: 0.5 }}>
              {userProfile.role}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666', display: 'block' }}>
              {userProfile.department}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666666', display: 'block' }}>
              {userProfile.phone}
            </Typography>
          </Box>
        </Box>
      </Menu>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onClose={handleCancelEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#2c2c2c', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <EditIcon size={24} />
          Edit Profile
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Avatar Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                p: 3,
                border: '2px dashed #e0e0e0',
                borderRadius: 2,
                mb: 3
              }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#2c2c2c', fontWeight: 'bold' }}>
                  Profile Picture
                </Typography>
                
                {/* Current Avatar Display */}
                <Box sx={{ mb: 2 }}>
                  {editedProfile.avatar ? (
                    <Avatar
                      src={editedProfile.avatar}
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        border: '4px solid #f59f1f'
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{ 
                        width: 100, 
                        height: 100, 
                        backgroundColor: '#f59f1f',
                        color: '#2c2c2c',
                        fontSize: '2rem',
                        fontWeight: 'bold',
                        border: '4px solid #f59f1f'
                      }}
                    >
                      {getUserInitials()}
                    </Avatar>
                  )}
                </Box>

                {/* Upload Button */}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  id="avatar-upload"
                />
                <label htmlFor="avatar-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CameraIcon size={20} />}
                    sx={{
                      color: '#2c2c2c',
                      borderColor: '#2c2c2c',
                      '&:hover': {
                        backgroundColor: '#2c2c2c',
                        color: '#ffffff',
                      }
                    }}
                  >
                    Upload New Photo
                  </Button>
                </label>

                {editedProfile.avatar && (
                  <Button
                    variant="text"
                    onClick={() => setEditedProfile({ ...editedProfile, avatar: undefined })}
                    sx={{ 
                      mt: 1, 
                      color: '#f44336',
                      fontSize: '0.875rem'
                    }}
                  >
                    Remove Photo
                  </Button>
                )}
              </Box>
            </Grid>

            {/* Form Fields */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="First Name"
                fullWidth
                variant="outlined"
                value={editedProfile.firstName}
                onChange={(e) => setEditedProfile({ ...editedProfile, firstName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Last Name"
                fullWidth
                variant="outlined"
                value={editedProfile.lastName}
                onChange={(e) => setEditedProfile({ ...editedProfile, lastName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Email Address"
                fullWidth
                variant="outlined"
                type="email"
                value={editedProfile.email}
                onChange={(e) => setEditedProfile({ ...editedProfile, email: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Phone Number"
                fullWidth
                variant="outlined"
                value={editedProfile.phone}
                onChange={(e) => setEditedProfile({ ...editedProfile, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Role/Position"
                fullWidth
                variant="outlined"
                value={editedProfile.role}
                onChange={(e) => setEditedProfile({ ...editedProfile, role: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Department"
                fullWidth
                variant="outlined"
                value={editedProfile.department}
                onChange={(e) => setEditedProfile({ ...editedProfile, department: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCancelEdit} sx={{ color: '#666666' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProfile} 
            variant="contained"
            sx={{
              backgroundColor: '#f59f1f',
              color: '#2c2c2c',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8901c',
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Crop Dialog */}
      <Dialog 
        open={imageCropOpen} 
        onClose={() => setImageCropOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '70vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#2c2c2c', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CropIcon size={24} />
          Crop Profile Picture
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#666666' }}>
            Drag to adjust the crop area. The image will be cropped to a square for your profile picture.
          </Typography>
          
          {imageSrc && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px'
            }}>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => handleCropComplete(c)}
                aspect={1} // Square crop
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  alt="Crop preview"
                  src={imageSrc}
                  style={{ maxWidth: '100%', maxHeight: '400px' }}
                />
              </ReactCrop>
            </Box>
          )}
          
          {/* Hidden canvas for generating cropped image */}
          <canvas
            ref={previewCanvasRef}
            style={{ display: 'none' }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => setImageCropOpen(false)} 
            sx={{ color: '#666666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCropSave}
            variant="contained"
            sx={{
              backgroundColor: '#f59f1f',
              color: '#2c2c2c',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8901c',
              }
            }}
          >
            Apply Crop
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Details Dialog */}
      <Dialog open={projectDetailsOpen} onClose={() => setProjectDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#2c2c2c', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <TaskIcon size={24} />
          Project Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedProject && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2 }}>
                  {selectedProject.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Chip 
                    label={selectedProject.status} 
                    sx={{
                      backgroundColor: selectedProject.status === 'Active' ? '#2c2c2c' : 
                                     selectedProject.status === 'Completed' ? '#f59f1f' : '#666666',
                      color: '#ffffff',
                      fontWeight: 'bold'
                    }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Progress: {calculateProgress(selectedProject.tasks)}%
                  </Typography>
                  {selectedProject.budgetedAmount > 0 && selectedProject.spentAmount > selectedProject.budgetedAmount && (
                    <Chip 
                      label={`⚠️ ${Math.round(((selectedProject.spentAmount - selectedProject.budgetedAmount) / selectedProject.budgetedAmount) * 100)}% OVER BUDGET`}
                      icon={<AlertCircle size={18} />}
                      sx={{
                        backgroundColor: '#c62828',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        fontSize: '0.85rem',
                        '& .MuiChip-icon': {
                          color: '#ffffff'
                        }
                      }}
                    />
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#2c2c2c' }}>
                  Project Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  {selectedProject.description}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ p: 2, backgroundColor: '#f0f4ff', borderRadius: 1, border: '1px solid #e0e8ff' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 1 }}>
                    Project Start Date
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#2c2c2c', mb: 2 }}>
                    {new Date(selectedProject.startDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                  {selectedProject.endDate && selectedProject.status === 'Completed' && (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 1 }}>
                        Project End Date
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#2c2c2c' }}>
                        {new Date(selectedProject.endDate as string).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#2c2c2c' }}>
                  Progress Overview
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateProgress(selectedProject.tasks)} 
                  sx={{ 
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: 'rgba(25, 22, 78, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#f59f1f',
                      borderRadius: 6
                    }
                  }} 
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#2c2c2c' }}>
                  Tasks ({selectedProject.tasks.filter(t => t.completed).length}/{selectedProject.tasks.length} completed)
                </Typography>
                <List>
                  {selectedProject.tasks.map((task) => (
                    <ListItem 
                      key={task.id} 
                      sx={{ 
                        px: 0,
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 22, 78, 0.05)'
                        }
                      }}
                      onClick={() => handleToggleTaskCompletion(selectedProject.id, task.id)}
                    >
                      <ListItemIcon>
                        <Box sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: task.completed ? '#4caf50' : '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}>
                          {task.completed && (
                            <Typography sx={{ color: 'white', fontSize: '12px' }}>✓</Typography>
                          )}
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={task.title}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip 
                              label={task.priority} 
                              size="small"
                              sx={{
                                backgroundColor: task.priority === 'High' ? '#f44336' : 
                                               task.priority === 'Medium' ? '#f59f1f' : '#4caf50',
                                color: '#ffffff',
                                fontSize: '0.75rem'
                              }}
                            />
                            {task.completed && task.actualCost !== undefined && (
                              <Chip 
                                label={`Cost: ${selectedProject.currency || '$'}${task.actualCost.toLocaleString()}`}
                                size="small"
                                icon={<DollarSign size={14} />}
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  fontSize: '0.75rem',
                                  '& .MuiChip-icon': {
                                    color: '#1976d2'
                                  }
                                }}
                              />
                            )}
                            <Typography variant="caption" sx={{ color: '#666666', fontStyle: 'italic' }}>
                              Click to toggle completion
                            </Typography>
                          </Box>
                        }
                        sx={{
                          '& .MuiListItemText-primary': {
                            textDecoration: task.completed ? 'line-through' : 'none',
                            color: task.completed ? '#666666' : '#000000',
                            fontWeight: task.completed ? 'normal' : '500'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setProjectDetailsOpen(false)} sx={{ color: '#666666' }}>
            Close
          </Button>
          <Button 
            onClick={() => {
              setProjectDetailsOpen(false);
              if (selectedProject) handleEditProject(selectedProject);
            }}
            variant="contained"
            sx={{
              backgroundColor: '#f59f1f',
              color: '#2c2c2c',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8901c',
              }
            }}
          >
            Edit Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Edit Dialog */}
      <Dialog open={projectEditOpen} onClose={handleCancelProjectEdit} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#2c2c2c', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <EditIcon size={24} />
          Edit Project
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {editingProject && (
            <Grid container spacing={3}>
              {/* Project Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2 }}>
                  Project Information
                </Typography>
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Project Name"
                  fullWidth
                  variant="outlined"
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                />
              </Grid>
              
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Project Description"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Progress (%)"
                  fullWidth
                  variant="outlined"
                  value={`${calculateProgress(editingProject.tasks)}%`}
                  disabled
                  helperText="Automatically calculated from completed tasks"
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#2c2c2c',
                      fontWeight: 'bold'
                    }
                  }}
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Status"
                  fullWidth
                  variant="outlined"
                  value={editingProject.status}
                  onChange={(e) => setEditingProject({ 
                    ...editingProject, 
                    status: e.target.value as 'In Progress' | 'Active' | 'Completed' | 'On Hold'
                  })}
                  helperText={
                    editingProject.tasks.length > 0 && 
                    editingProject.tasks.filter(t => t.completed).length === editingProject.tasks.length
                      ? "Status automatically set to 'Completed' - all tasks done!"
                      : editingProject.status === 'Completed' && 
                        editingProject.tasks.filter(t => t.completed).length < editingProject.tasks.length
                      ? "Status will change to 'Active' - not all tasks completed"
                      : "You can manually change the project status"
                  }
                  SelectProps={{
                    native: true,
                  }}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      color: editingProject.tasks.length > 0 && 
                             editingProject.tasks.filter(t => t.completed).length === editingProject.tasks.length
                        ? '#4caf50' // Green for completed
                        : '#666666' // Default color
                    }
                  }}
                >
                  <option value="In Progress">In Progress</option>
                  <option value="Active">Active</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Project Start Date"
                  fullWidth
                  type="date"
                  variant="outlined"
                  value={editingProject.startDate}
                  onChange={(e) => setEditingProject({ ...editingProject, startDate: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Task Management Section */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2, mt: 2 }}>
                  Project Tasks
                </Typography>
              </Grid>

              {/* Add New Task */}
              <Grid size={{ xs: 12 }}>
                <Box sx={{ 
                  p: 3, 
                  border: '2px dashed #e0e0e0', 
                  borderRadius: 2,
                  backgroundColor: '#f9f9f9'
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Add New Task
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 8 }}>
                      <TextField
                        label="Task Title"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddTaskToEditingProject();
                          }
                        }}
                      />
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        select
                        label="Priority"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={newTask.priority}
                        onChange={(e) => setNewTask({ 
                          ...newTask, 
                          priority: e.target.value as 'High' | 'Medium' | 'Low' 
                        })}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </TextField>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 1 }}>
                      <Button
                        variant="contained"
                        onClick={handleAddTaskToEditingProject}
                        disabled={!newTask.title.trim()}
                        sx={{
                          backgroundColor: '#f59f1f',
                          color: '#2c2c2c',
                          minWidth: '40px',
                          '&:hover': {
                            backgroundColor: '#e8901c',
                          },
                          '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#999999',
                          }
                        }}
                      >
                        <AddIcon size={20} />
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Task List */}
              {editingProject.tasks.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Tasks ({editingProject.tasks.filter(t => t.completed).length}/{editingProject.tasks.length} completed)
                  </Typography>
                  <List sx={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1, 
                    backgroundColor: '#ffffff',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {editingProject.tasks.map((task, index) => (
                      <ListItem 
                        key={task.id}
                        sx={{ 
                          borderBottom: index < editingProject.tasks.length - 1 ? '1px solid #f0f0f0' : 'none',
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        }}
                      >
                        {editingTaskId === task.id ? (
                          // Editing Mode
                          <>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  backgroundColor: task.completed ? '#4caf50' : '#e0e0e0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                {task.completed && (
                                  <Typography sx={{ color: 'white', fontSize: '12px' }}>✓</Typography>
                                )}
                              </Box>
                            </ListItemIcon>
                            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                value={editingTaskData.title}
                                onChange={(e) => setEditingTaskData({ ...editingTaskData, title: e.target.value })}
                                size="small"
                                sx={{ flexGrow: 1 }}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEditingTask();
                                  }
                                }}
                              />
                              <TextField
                                select
                                value={editingTaskData.priority}
                                onChange={(e) => setEditingTaskData({ 
                                  ...editingTaskData, 
                                  priority: e.target.value as 'High' | 'Medium' | 'Low' 
                                })}
                                size="small"
                                sx={{ minWidth: '80px' }}
                                SelectProps={{
                                  native: true,
                                }}
                              >
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                              </TextField>
                              <Button
                                onClick={handleSaveEditingTask}
                                disabled={!editingTaskData.title.trim()}
                                sx={{ 
                                  color: '#4caf50',
                                  minWidth: '32px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)'
                                  }
                                }}
                              >
                                <CheckIcon size={18} />
                              </Button>
                              <Button
                                onClick={handleCancelEditingTask}
                                sx={{ 
                                  color: '#f44336',
                                  minWidth: '32px',
                                  '&:hover': {
                                    backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                  }
                                }}
                              >
                                <XIcon size={18} />
                              </Button>
                            </Box>
                          </>
                        ) : (
                          // View Mode
                          <>
                            <ListItemIcon>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  backgroundColor: task.completed ? '#4caf50' : '#e0e0e0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s ease'
                                }}
                              >
                                {task.completed && (
                                  <Typography sx={{ color: 'white', fontSize: '12px' }}>✓</Typography>
                                )}
                              </Box>
                            </ListItemIcon>
                            <ListItemText 
                              primary={task.title}
                              secondary={
                                <Chip 
                                  label={task.priority} 
                                  size="small"
                                  sx={{
                                    backgroundColor: task.priority === 'High' ? '#f44336' : 
                                                   task.priority === 'Medium' ? '#f59f1f' : '#4caf50',
                                    color: '#ffffff',
                                    fontSize: '0.75rem',
                                    mt: 0.5
                                  }}
                                />
                              }
                              sx={{
                                '& .MuiListItemText-primary': {
                                  textDecoration: task.completed ? 'line-through' : 'none',
                                  color: task.completed ? '#666666' : '#000000',
                                  fontWeight: task.completed ? 'normal' : '500'
                                }
                              }}
                            />
                            <Button
                              onClick={() => handleStartEditingTask(task)}
                              sx={{ 
                                color: '#2c2c2c',
                                minWidth: '32px',
                                '&:hover': {
                                  backgroundColor: 'rgba(25, 22, 78, 0.1)'
                                }
                              }}
                            >
                              <EditIcon size={16} />
                            </Button>
                            <Button
                              onClick={() => handleRemoveTaskFromEditingProject(task.id)}
                              sx={{ 
                                color: '#f44336',
                                minWidth: '32px',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                }
                              }}
                            >
                              <XIcon size={18} />
                            </Button>
                          </>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCancelProjectEdit} sx={{ color: '#666666' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProject} 
            variant="contained"
            disabled={!editingProject?.name || !editingProject?.description}
            sx={{
              backgroundColor: '#f59f1f',
              color: '#2c2c2c',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8901c',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999999',
              }
            }}
          >
            Save Changes {editingProject && `(${editingProject.tasks.length} tasks)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          backgroundColor: '#2c2c2c', 
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <AddIcon size={24} />
          Create New Project
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Project Details */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2 }}>
                Project Information
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                autoFocus
                label="Project Name"
                fullWidth
                variant="outlined"
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Project Description"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              />
            </Grid>

            {/* Budget Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2 }}>
                Project Budget
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Budgeted Amount"
                fullWidth
                type="number"
                variant="outlined"
                value={newProject.budgetedAmount}
                onChange={(e) => setNewProject({ ...newProject, budgetedAmount: parseFloat(e.target.value) || 0 })}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                label="Currency"
                fullWidth
                variant="outlined"
                value={newProject.currency}
                onChange={(e) => setNewProject({ ...newProject, currency: e.target.value })}
              >
                <MenuItem value="ZMW">ZMW (Zambian Kwacha)</MenuItem>
                <MenuItem value="USD">USD (US Dollar)</MenuItem>
                <MenuItem value="ZAR">ZAR (South African Rand)</MenuItem>
                <MenuItem value="EUR">EUR (Euro)</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Project Start Date"
                fullWidth
                type="date"
                variant="outlined"
                value={newProject.startDate}
                onChange={(e) => setNewProject({ ...newProject, startDate: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Task Management Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c2c2c', mb: 2, mt: 2 }}>
                Project Tasks
              </Typography>
            </Grid>

            {/* Add New Task */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ 
                p: 3, 
                border: '2px dashed #e0e0e0', 
                borderRadius: 2,
                backgroundColor: '#f9f9f9'
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Add Task
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <TextField
                      label="Task Title"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={newTask.title}
                      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddTask();
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      select
                      label="Priority"
                      fullWidth
                      variant="outlined"
                      size="small"
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ 
                        ...newTask, 
                        priority: e.target.value as 'High' | 'Medium' | 'Low' 
                      })}
                      SelectProps={{
                        native: true,
                      }}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </TextField>
                  </Grid>
                  
                  <Grid size={{ xs: 12, sm: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleAddTask}
                      disabled={!newTask.title.trim()}
                      sx={{
                        backgroundColor: '#f59f1f',
                        color: '#2c2c2c',
                        minWidth: '40px',
                        '&:hover': {
                          backgroundColor: '#e8901c',
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0',
                          color: '#999999',
                        }
                      }}
                    >
                      <AddIcon size={20} />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Task List */}
            {newProject.tasks.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Tasks ({newProject.tasks.length})
                </Typography>
                <List sx={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1, 
                  backgroundColor: '#ffffff',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {newProject.tasks.map((task, index) => (
                    <ListItem 
                      key={index}
                      sx={{ 
                        borderBottom: index < newProject.tasks.length - 1 ? '1px solid #f0f0f0' : 'none',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <TaskIcon size={20} color="#2c2c2c" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={task.title}
                        secondary={
                          <Chip 
                            label={task.priority} 
                            size="small"
                            sx={{
                              backgroundColor: task.priority === 'High' ? '#f44336' : 
                                             task.priority === 'Medium' ? '#f59f1f' : '#4caf50',
                              color: '#ffffff',
                              fontSize: '0.75rem',
                              mt: 0.5
                            }}
                          />
                        }
                      />
                      <Button
                        onClick={() => handleRemoveTask(index)}
                        sx={{ 
                          color: '#f44336',
                          minWidth: '32px',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)'
                          }
                        }}
                      >
                        <XIcon size={18} />
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={() => {
              setDialogOpen(false);
              setNewProject({ name: '', description: '', budgetedAmount: 0, currency: 'ZMW', startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], tasks: [] });
              setNewTask({ title: '', priority: 'Medium' });
            }} 
            sx={{ color: '#666666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateProject} 
            variant="contained"
            disabled={!newProject.name || !newProject.description}
            sx={{
              backgroundColor: '#f59f1f',
              color: '#2c2c2c',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e8901c',
              },
              '&:disabled': {
                backgroundColor: '#e0e0e0',
                color: '#999999',
              }
            }}
          >
            Create Project {newProject.tasks.length > 0 && `(${newProject.tasks.length} tasks)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Cost Input Dialog */}
      <Dialog 
        open={taskCostDialogOpen} 
        onClose={() => {
          setTaskCostDialogOpen(false);
          setTaskBeingCompleted(null);
          setTaskCost(0);
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f8f9fa', color: '#2c2c2c', fontWeight: 'bold' }}>
          Enter Task Cost
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please enter the actual cost incurred for completing this task:
          </Typography>
          <TextField
            autoFocus
            label="Actual Cost"
            type="number"
            fullWidth
            variant="outlined"
            value={taskCost}
            onChange={(e) => setTaskCost(parseFloat(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              startAdornment: selectedProject && (
                <Typography sx={{ mr: 1, color: '#666' }}>
                  {selectedProject.currency}
                </Typography>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This cost will be added to the project's total spent amount
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setTaskCostDialogOpen(false);
              setTaskBeingCompleted(null);
              setTaskCost(0);
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTaskCost}
            variant="contained"
            sx={{
              backgroundColor: '#f59f1f',
              color: 'white',
              '&:hover': { backgroundColor: '#e08f0f' }
            }}
          >
            Complete Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Date Dialog */}
      <Dialog 
        open={endDateDialogOpen} 
        onClose={() => {
          setEndDateDialogOpen(false);
          setProjectNeedingEndDate(null);
          setSelectedEndDate(new Date().toISOString().split('T')[0]);
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#f8f9fa', color: '#2c2c2c', fontWeight: 'bold' }}>
          Set Project End Date
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            All tasks in this project are now complete! Please enter the project end date:
          </Typography>
          <TextField
            autoFocus
            label="End Date"
            type="date"
            fullWidth
            variant="outlined"
            value={selectedEndDate}
            onChange={(e) => setSelectedEndDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setEndDateDialogOpen(false);
              setProjectNeedingEndDate(null);
              setSelectedEndDate(new Date().toISOString().split('T')[0]);
            }}
            sx={{ color: '#666666' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEndDate}
            variant="contained"
            sx={{
              backgroundColor: '#f59f1f',
              color: 'white',
              '&:hover': { backgroundColor: '#e08f0f' }
            }}
          >
            Save End Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New User Dialog */}
      <Dialog 
        open={newUserDialogOpen} 
        onClose={handleNewUserCancel}
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa', 
          color: '#2c2c2c', 
          fontWeight: 'bold',
          borderBottom: '1px solid #e0e0e0'
        }}>
          Add New User
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={newUserData.firstName}
                onChange={(e) => setNewUserData({...newUserData, firstName: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={newUserData.lastName}
                onChange={(e) => setNewUserData({...newUserData, lastName: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Role"
                value={newUserData.role}
                onChange={(e) => setNewUserData({...newUserData, role: e.target.value as 'admin' | 'manager' | 'employee'})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              >
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Department"
                value={newUserData.department}
                onChange={(e) => setNewUserData({...newUserData, department: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newUserData.phone}
                onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleNewUserCancel}
            sx={{ 
              color: '#666666',
              borderColor: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleNewUserSave}
            sx={{
              backgroundColor: '#f59f1f',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e08f0f'
              }
            }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog 
        open={editUserDialogOpen} 
        onClose={handleEditUserCancel}
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f8f9fa', 
          color: '#2c2c2c', 
          fontWeight: 'bold',
          borderBottom: '1px solid #e0e0e0'
        }}>
          Edit User - {editingUser?.profile.firstName} {editingUser?.profile.lastName}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={editUserData.firstName}
                onChange={(e) => setEditUserData({...editUserData, firstName: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={editUserData.lastName}
                onChange={(e) => setEditUserData({...editUserData, lastName: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={editUserData.email}
                onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Role"
                value={editUserData.role}
                onChange={(e) => setEditUserData({...editUserData, role: e.target.value as 'admin' | 'manager' | 'employee'})}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              >
                <MenuItem value="employee">Employee</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Administrator</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Status"
                value={editUserData.isActive ? 'active' : 'inactive'}
                onChange={(e) => setEditUserData({...editUserData, isActive: e.target.value === 'active'})}
                disabled={editingUser?.id === currentUser.id} // Can't change your own status
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Department"
                value={editUserData.department}
                onChange={(e) => setEditUserData({...editUserData, department: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editUserData.phone}
                onChange={(e) => setEditUserData({...editUserData, phone: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#f59f1f' },
                  },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#f59f1f' },
                }}
              />
            </Grid>
            {editingUser?.id === currentUser.id && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc02' }}>
                  <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                    ℹ️ Note: You are editing your own profile. Some restrictions apply:
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#e65100' }}>
                    • You cannot change your account status<br/>
                    • Role changes may affect your permissions
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleEditUserCancel}
            sx={{ 
              color: '#666666',
              borderColor: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(102, 102, 102, 0.1)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleEditUserSave}
            sx={{
              backgroundColor: '#f59f1f',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#e08f0f'
              }
            }}
          >
            Update User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

// Main App component with authentication
interface AuthState {
  isAuthenticated: boolean;
  currentUser: AppUser | null;
  users: AppUser[];
}

function App() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    currentUser: null,
    users: defaultUsers // Initialize with default users immediately
  });

  // Initialize users database and check authentication on mount
  useEffect(() => {
    const initializeAuth = () => {
      // Version control for users database - increment this when adding new default users
      const USERS_DB_VERSION = 2; // Changed from 1 to 2 to force update
      const storedVersion = localStorage.getItem('app_users_version');
      
      // Initialize users database if not exists or version mismatch
      let users = defaultUsers;
      const savedUsers = localStorage.getItem('app_users');
      
      // Force update if version is different or no saved users
      if (!savedUsers || storedVersion !== USERS_DB_VERSION.toString()) {
        console.log('Updating users database to version', USERS_DB_VERSION);
        localStorage.setItem('app_users', JSON.stringify(defaultUsers));
        localStorage.setItem('app_users_version', USERS_DB_VERSION.toString());
        users = defaultUsers;
      } else {
        try {
          const parsedUsers = JSON.parse(savedUsers);
          // Validate the parsed users have the expected structure
          if (Array.isArray(parsedUsers) && parsedUsers.length > 0) {
            users = parsedUsers;
          } else {
            localStorage.setItem('app_users', JSON.stringify(defaultUsers));
            localStorage.setItem('app_users_version', USERS_DB_VERSION.toString());
          }
        } catch (error) {
          console.error('Error loading users:', error);
          // Fall back to default users
          localStorage.setItem('app_users', JSON.stringify(defaultUsers));
          localStorage.setItem('app_users_version', USERS_DB_VERSION.toString());
        }
      }

      // Check if user is already authenticated
      const currentUserData = localStorage.getItem('currentUser');
      const authToken = localStorage.getItem('authToken');
      
      if (currentUserData && authToken) {
        try {
          const storedUser = JSON.parse(currentUserData);
          // Find the most up-to-date user profile from the users database
          const latestUser = users.find(u => u.id === storedUser.id);
          
          if (latestUser) {
            setAuthState({
              isAuthenticated: true,
              currentUser: latestUser,
              users
            });
            // Update localStorage with latest user data
            localStorage.setItem('currentUser', JSON.stringify(latestUser));
          } else {
            // User not found in database, logout
            setAuthState({
              isAuthenticated: false,
              currentUser: null,
              users
            });
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          setAuthState({
            isAuthenticated: false,
            currentUser: null,
            users
          });
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          currentUser: null,
          users
        });
      }
    };
    
    initializeAuth();
  }, []);

  const handleLogin = (user: AppUser) => {
    setAuthState({
      isAuthenticated: true,
      currentUser: user,
      users: authState.users
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken');
    setAuthState({
      isAuthenticated: false,
      currentUser: null,
      users: authState.users
    });
  };

  // Show login page if not authenticated  
  if (!authState.isAuthenticated) {
    return (
      <SimpleLogin onLogin={handleLogin} users={authState.users} />
    );
  }

  return (
    <AuthenticatedApp onLogout={handleLogout} currentUser={authState.currentUser!} />
  );
}

export default App;
